/**
 * useSubscription — Subscription watchdog hook
 *
 * Polls GET /api/v1/module/subscription-status every 5 minutes and maintains
 * a live countdown using the periodEnd date returned by Main Hall.
 *
 * Returns:
 *   loading          — true on first render before the first response
 *   valid            — true if the subscription is active and not expired
 *   isExpired        — true when status=expired OR local clock passed periodEnd
 *   isLifetime       — true for lifetime subscriptions (no countdown)
 *   status           — raw status string from Main Hall
 *   pricingModel     — monthly | annual | lifetime
 *   monthsRemaining  — calendar months until periodEnd (0 if expired)
 *   daysRemaining    — remainder days   (< ~30, after months)
 *   hoursRemaining   — remainder hours  (< 24)
 *   minutesRemaining — remainder minutes (< 60)
 *   secondsRemaining — remainder seconds (< 60) — ticks live every second
 *   periodStart      — ISO date string (UTC)
 *   periodEnd        — ISO date string (UTC)  ← used for the live countdown
 *   businessName     — hotel name from Main Hall
 *   urgency          — 'ok' | 'warning' (≤7d) | 'critical' (≤1d) | 'expired'
 *   refetch()        — manually re-poll Main Hall immediately
 *
 * Design notes:
 *   - Polls every 5 min (POLL_INTERVAL_MS).
 *   - Ticks every second (TICK_INTERVAL_MS) so the badge behaves like a real
 *     watch — months/days/hours/minutes/seconds all stay live between polls.
 *   - Client-side expiry: when the local clock passes periodEnd the hook flips
 *     isExpired=true immediately — no need to wait for the next poll. This is
 *     what fires the lockout overlay the instant the subscription ends.
 *   - Fail-open: a network error during polling keeps the last known state; it
 *     does NOT flip isExpired. Main Hall outages don't lock staff out.
 *   - Custom events: dispatch hotelmis:subscription:renewed to reset the lockout
 *     after a tenant admin renews in a different tab.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getToken, getTenantSlug } from '../services/api.js';

export const POLL_INTERVAL_MS = 5 * 60 * 1000;   // 5 minutes
export const TICK_INTERVAL_MS = 1000;              // 1 second countdown tick

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute a calendar-aware months/days/hours/minutes/seconds breakdown from
 * now until periodEnd — like a normal countdown watch.
 *
 * Months are calculated using real calendar months (so "1 month" from
 * Jan 31 → Feb 28/29 etc. behaves correctly), and the remainder is broken
 * down into days/hours/minutes/seconds. All values are >= 0 — never negative.
 */
function computeRemaining(periodEnd) {
  const empty = { total: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  if (!periodEnd) return empty;

  const now = new Date();
  const end = new Date(periodEnd);
  const total = end.getTime() - now.getTime();
  if (total <= 0) return empty;

  // Calendar-aware month count
  let months = (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
  let anchor = new Date(now);
  anchor.setMonth(anchor.getMonth() + months);
  if (anchor.getTime() > end.getTime()) {
    months -= 1;
    anchor = new Date(now);
    anchor.setMonth(anchor.getMonth() + months);
  }
  if (months < 0) months = 0;

  const remainderMs = end.getTime() - anchor.getTime();
  const days    = Math.floor(remainderMs / 86_400_000);
  const hours   = Math.floor((remainderMs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remainderMs % 3_600_000)  / 60_000);
  const seconds = Math.floor((remainderMs % 60_000)     / 1000);

  return { total, months, days, hours, minutes, seconds };
}

/**
 * Derive urgency from remaining time.
 *   ok       → green  (> 7 days or lifetime)
 *   warning  → yellow (≤ 7 days)
 *   critical → red    (≤ 1 day)
 *   expired  → dark   (subscription over)
 */
function deriveUrgency(months, days, isExpired, isLifetime) {
  if (isLifetime) return 'ok';
  if (isExpired)  return 'expired';
  if (months > 0) return 'ok';
  if (days <= 1)  return 'critical';
  if (days <= 7)  return 'warning';
  return 'ok';
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useSubscription() {
  // Raw data returned by the backend
  const [raw,        setRaw]        = useState(null);
  // True when Main Hall is unreachable (network error or non-OK response)
  const [fetchFailed, setFetchFailed] = useState(false);
  // Minute tick counter — forces re-render so the countdown stays live
  const [tick, setTick] = useState(0);

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    const token  = getToken();
    const tenant = getTenantSlug();
    if (!token || !tenant) return;

    try {
      const res = await fetch('/api/v1/module/subscription-status', {
        headers: {
          Authorization:   `Bearer ${token}`,
          'X-Tenant-Slug': tenant,
          Accept:          'application/json',
        },
        signal: AbortSignal.timeout(8_000),
      });
      if (!res.ok) {
        // Non-OK response — Main Hall is reachable but returned an error
        setFetchFailed(true);
        return;   // keep last known state
      }
      const json = await res.json();
      const data = json?.data ?? json;
      setRaw(data);
      setFetchFailed(false);   // clear any previous failure flag on success
    } catch (_) {
      // Network error / timeout — Main Hall is unreachable
      // Fail-open: keep last known state, don't flip isExpired
      setFetchFailed(true);
    }
  }, []);

  // ── Effects ──────────────────────────────────────────────────────────────

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling
  useEffect(() => {
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  // Minute tick for live countdown display
  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(tickRef.current);
  }, []);

  // Listen for renewal / expiry events fired from other tabs or the
  // SubscriptionWatchdog "Refresh" button after a tenant admin renews.
  useEffect(() => {
    const onRenewed = () => fetchStatus();
    const onExpired = () => fetchStatus();
    window.addEventListener('hotelmis:subscription:renewed', onRenewed);
    window.addEventListener('hotelmis:subscription:expired', onExpired);
    return () => {
      window.removeEventListener('hotelmis:subscription:renewed', onRenewed);
      window.removeEventListener('hotelmis:subscription:expired', onExpired);
    };
  }, [fetchStatus]);

  // ── Derived state ─────────────────────────────────────────────────────────

  // Loading until we get the first response
  if (!raw) return { loading: true, unreachable: fetchFailed };

  const isLifetime   = raw.pricingModel === 'lifetime' || raw.daysRemaining >= 36_000;
  const remaining    = isLifetime
    ? { total: Infinity, months: 0, days: 36_500, hours: 0, minutes: 0, seconds: 0 }
    : computeRemaining(raw.currentPeriodEnd);

  // Client-side expiry: flip immediately when local clock passes periodEnd.
  // This fires the lockout overlay even before the next 5-min poll.
  const clientExpired = !isLifetime
    && raw.currentPeriodEnd != null
    && new Date(raw.currentPeriodEnd).getTime() < Date.now();

  const isExpired = clientExpired
    || raw.status === 'expired'
    || raw.status === 'cancelled'
    || raw.status === 'suspended'
    || raw.status === 'not_subscribed'
    || raw.status === 'not_found';

  const isValid = !isExpired && Boolean(raw.valid);

  const urgency = deriveUrgency(remaining.months, remaining.days, isExpired, isLifetime);

  // Detect a degenerate payload: Main Hall is sometimes reachable (HTTP 200) but
  // returns an error/fallback body with no currentPeriodEnd and no valid flag.
  // This collapses the countdown to "0 minutes" which is misleading — treat it
  // as unreachable so the user sees the amber banner instead of a false alarm.
  const dataDegenerate = !isLifetime
    && !isExpired
    && !raw.currentPeriodEnd
    && !raw.valid;

  return {
    loading:          false,
    raw,
    // Core
    valid:            isValid,
    isExpired,
    isLifetime,
    unreachable:      fetchFailed || dataDegenerate,
    status:           raw.status       ?? 'unknown',
    pricingModel:     raw.pricingModel ?? null,
    // Countdown
    monthsRemaining:  remaining.months,
    daysRemaining:    remaining.days,
    hoursRemaining:   remaining.hours,
    minutesRemaining: remaining.minutes,
    secondsRemaining: remaining.seconds,
    // Dates
    periodStart:      raw.currentPeriodStart ?? null,
    periodEnd:        raw.currentPeriodEnd   ?? null,
    // Meta
    businessName:     raw.businessName ?? null,
    urgency,
    // Actions
    refetch:          fetchStatus,
  };
}